/**
 * Preferred date format options (not limited to this list for future extension).
 * Used in user settings to normalise date display across the app and reports.
 */
export const PREFERRED_DATE_FORMATS = [
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY/MM/DD',
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'YYYY-MM-DD',
] as const;

export type PreferredDateFormat = (typeof PREFERRED_DATE_FORMATS)[number];

export interface FormatDateOptions {
  showTime?: boolean;
  /** IANA timezone (e.g. 'Europe/London'). Dates are shown in this zone; omit for device local. */
  timeZone?: string;
  /** When false, time is shown in 12-hour format with AM/PM; when true or omitted, 24-hour. */
  use24Hour?: boolean;
}

/**
 * Format a date (Date, ISO string, or timestamp) using a preferred format and optional timezone.
 * Use this everywhere dates are displayed so the app stays consistent.
 *
 * @param dateInput - Date instance, ISO string, or ms timestamp
 * @param format - One of PREFERRED_DATE_FORMATS
 * @param options - Optional: showTime, timeZone (IANA)
 * @returns Formatted string or '-' if invalid
 */
export function formatDateByPreference(
  dateInput: Date | string | number | null | undefined,
  format: PreferredDateFormat,
  options?: FormatDateOptions
): string {
  if (dateInput == null) return '-';
  try {
    const date = typeof dateInput === 'object' && 'getTime' in dateInput
      ? dateInput
      : new Date(dateInput);
    if (isNaN(date.getTime())) return '-';

    const tz = options?.timeZone;
    const use24Hour = options?.use24Hour !== false; // default true for backward compat when not passed
    let day: string, month: string, year: string, hours: string, minutes: string;
    let dayPeriod: string | undefined; // 'AM' | 'PM' when hour12

    if (tz) {
      const opts: Intl.DateTimeFormatOptions = {
        timeZone: tz,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: !use24Hour,
      };
      const formatter = new Intl.DateTimeFormat('en-CA', opts);
      const parts = formatter.formatToParts(date);
      day = parts.find(p => p.type === 'day')?.value ?? '';
      month = parts.find(p => p.type === 'month')?.value ?? '';
      year = parts.find(p => p.type === 'year')?.value ?? '';
      hours = parts.find(p => p.type === 'hour')?.value ?? '00';
      minutes = parts.find(p => p.type === 'minute')?.value ?? '00';
      if (!use24Hour) {
        const period = parts.find(p => p.type === 'dayPeriod')?.value;
        dayPeriod = period ? period.toUpperCase() : undefined;
      }
    } else {
      day = String(date.getDate()).padStart(2, '0');
      month = String(date.getMonth() + 1).padStart(2, '0');
      year = String(date.getFullYear());
      const h = date.getHours();
      const m = date.getMinutes();
      if (use24Hour) {
        hours = String(h).padStart(2, '0');
        minutes = String(m).padStart(2, '0');
      } else {
        const h12 = h % 12 || 12;
        hours = String(h12);
        minutes = String(m).padStart(2, '0');
        dayPeriod = h < 12 ? 'AM' : 'PM';
      }
    }

    let formatted: string;
    switch (format) {
      case 'DD/MM/YYYY':
        formatted = `${day}/${month}/${year}`;
        break;
      case 'MM/DD/YYYY':
        formatted = `${month}/${day}/${year}`;
        break;
      case 'YYYY/MM/DD':
        formatted = `${year}/${month}/${day}`;
        break;
      case 'DD-MM-YYYY':
        formatted = `${day}-${month}-${year}`;
        break;
      case 'MM-DD-YYYY':
        formatted = `${month}-${day}-${year}`;
        break;
      case 'YYYY-MM-DD':
        formatted = `${year}-${month}-${day}`;
        break;
      default:
        formatted = `${day}/${month}/${year}`;
    }
    if (options?.showTime) {
      const timeStr = dayPeriod ? `${hours}:${minutes} ${dayPeriod}` : `${hours}:${minutes}`;
      formatted += ` ${timeStr}`;
    }
    return formatted;
  } catch {
    return '-';
  }
}

/**
 * Get timezone abbreviation for a date (e.g. "AEST", "PST").
 */
export function getTimeZoneAbbreviation(
  dateInput: Date | string | null | undefined,
  timeZone?: string
): string {
  if (dateInput == null) return '';
  try {
    const date = typeof dateInput === 'object' && 'getTime' in dateInput
      ? dateInput
      : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: timeZone ?? undefined,
      timeZoneName: 'short',
    }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Format datetime for context bar using preferred date format + time + timezone abbreviation.
 * e.g. "05/02/2026 4:51 PM (AEST)"
 */
export function formatDateTimeWithTz(
  dateInput: Date | string | null | undefined,
  options: {
    timeZone?: string;
    use24Hour?: boolean;
    dateFormat?: PreferredDateFormat;
  } = {}
): string {
  if (dateInput == null) return '';
  try {
    const date = typeof dateInput === 'object' && 'getTime' in dateInput
      ? dateInput
      : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const format = options.dateFormat ?? 'DD/MM/YYYY';
    const dateTimeStr = formatDateByPreference(date, format, {
      showTime: true,
      timeZone: options.timeZone,
      use24Hour: options.use24Hour,
    });
    const tzAbbr = getTimeZoneAbbreviation(date, options.timeZone);
    return tzAbbr ? `${dateTimeStr} (${tzAbbr})` : dateTimeStr;
  } catch {
    return '';
  }
}

/**
 * UTC tooltip string for auditability, e.g. "Imported at: 2026-02-05 06:51:30 UTC"
 */
export function formatUtcTooltip(dateInput: Date | string | null | undefined, prefix = 'Imported at: '): string {
  if (dateInput == null) return '';
  try {
    const date = typeof dateInput === 'object' && 'getTime' in dateInput
      ? dateInput
      : new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const utc = date.toISOString().replace('T', ' ').slice(0, 19);
    return `${prefix}${utc} UTC`;
  } catch {
    return '';
  }
}

// Intl.supportedValuesOf is ES2023; type for older TS libs
const IntlWithSupportedValuesOf = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };

/** Get list of IANA timezone identifiers for dropdowns. Uses Intl.supportedValuesOf when available. */
export function getTimeZoneOptions(): { value: string; label: string }[] {
  let ids: string[] = [];
  try {
    if (typeof IntlWithSupportedValuesOf.supportedValuesOf === 'function') {
      ids = IntlWithSupportedValuesOf.supportedValuesOf('timeZone') as string[];
    }
  } catch {
    // fallback: common timezones
    ids = [
      'UTC',
      'Europe/London',
      'Europe/Paris',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Australia/Sydney',
      'Asia/Tokyo',
      'Asia/Singapore',
    ];
  }
  ids.sort();
  return ids.map(value => {
    try {
      const offset = new Intl.DateTimeFormat('en-GB', { timeZone: value, timeZoneName: 'shortOffset' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value ?? '';
      // Display label: replace underscores with spaces for readability (e.g. "America/New York")
      const displayName = value.replace(/_/g, ' ');
      const label = offset ? `${displayName} (${offset})` : displayName;
      return { value, label };
    } catch {
      return { value, label: value.replace(/_/g, ' ') };
    }
  });
}
